import React, { useMemo, useState } from 'react';
import Typography from 'apollo-react/components/Typography';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import IconButton from 'apollo-react/components/IconButton';
import Mailbox from 'apollo-react-icons/Mailbox';
import CloseCircle from 'apollo-react-icons/CloseCircle';
import User2 from 'apollo-react-icons/User2';
import StatusCheck2 from 'apollo-react-icons/StatusCheck2';
import AlertTriangle from 'apollo-react-icons/AlertTriangle';
import {
  HomeDashboardChartsSection,
  homePageGridItemStyle,
} from '../HomeDashboardCharts';
import { useHomeDashboard } from '../../../hooks/features/dashboard/useHomeDashboard';
import { createDefaultDashboardDateFilter } from '../../../utils/dashboardDateRangeUtils';
import DashboardDateRangeFilter from './DashboardDateRangeFilter';
import styles from './HomeDashboard.module.css';

const cardStyle = {
  padding: '0px 14px 15px 14px',
  borderRadius: '17px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #e6e6e6',
};

const activeCardStyle = {
  padding: '0px 14px 15px 14px',
  borderRadius: '17px',
  backgroundColor: '#423F3F',
};

const cardHeaderStyle = { paddingBottom: '4px' };
const activeTasksStyle = { color: '#FFFFFF' };
const closedTasksStyle = { color: '#4CAF50' };
const rejectedRequestsStyle = { color: '#fc2112' };
const warningStyle = { color: '#f5a623' };
const cardHeaderIconStyle = { fontSize: '36px' };
const bodyTypoStyle = { fontSize: '24px', fontWeight: 'bold' };

function KpiCard({
  title,
  value,
  cardVariant = 'default',
  titleStyle,
  icon,
  iconColor,
}) {
  const isActive = cardVariant === 'active';
  return (
    <div className={styles.kpiItem}>
      <div style={homePageGridItemStyle}>
        <Card
          tabIndex={0}
          interactive
          style={isActive ? activeCardStyle : cardStyle}
        >
          <CardHeader
            title={title}
            style={cardHeaderStyle}
            titleTypographyProps={{ style: titleStyle }}
            action={
              <IconButton
                disabled
                aria-label={title}
                style={{ ...cardHeaderIconStyle, color: iconColor }}
              >
                {icon}
              </IconButton>
            }
          />
          <CardContent>
            <Typography
              style={{
                ...bodyTypoStyle,
                ...(isActive ? { color: activeTasksStyle.color } : {}),
              }}
            >
              {value}
            </Typography>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   persona: 'admin' | 'super-admin' | 'customer',
 *   showReviewKpis?: boolean,
 *   showSlaKpis?: boolean,
 *   showDraftKpi?: boolean,
 * }} props
 */
export function HomeDashboard({
  persona,
  showReviewKpis = true,
  showSlaKpis = true,
  showDraftKpi = false,
}) {
  const [dateFilter, setDateFilter] = useState(
    createDefaultDashboardDateFilter
  );

  const {
    kpis,
    statusBarData,
    priorityBarData,
    slaAtRiskRows,
    showPaymentCharts,
    loading,
  } = useHomeDashboard(persona, dateFilter);

  const kpiCards = useMemo(() => {
    const displayValue = (key) => (loading ? '—' : (kpis[key] ?? 0));
    const cards = [
      {
        key: 'activeTasks',
        title: 'Active Tasks',
        value: displayValue('activeTasks'),
        cardVariant: 'active',
        titleStyle: activeTasksStyle,
        icon: <Mailbox />,
        iconColor: activeTasksStyle.color,
      },
      {
        key: 'closedTasks',
        title: 'Closed Tasks',
        value: displayValue('closedTasks'),
        titleStyle: closedTasksStyle,
        icon: <StatusCheck2 />,
        iconColor: closedTasksStyle.color,
      },
      {
        key: 'newRequests',
        title: 'New Requests',
        value: displayValue('newRequests'),
        icon: <User2 />,
      },
      {
        key: 'rejectedRequests',
        title: 'Rejected Requests',
        value: displayValue('rejectedRequests'),
        titleStyle: rejectedRequestsStyle,
        icon: <CloseCircle />,
        iconColor: rejectedRequestsStyle.color,
      },
    ];

    if (showSlaKpis) {
      cards.push(
        {
          key: 'slaAtRisk',
          title: 'SLA at risk',
          value: displayValue('slaAtRisk'),
          titleStyle: warningStyle,
          icon: <AlertTriangle />,
          iconColor: warningStyle.color,
        },
        {
          key: 'slaBreached',
          title: 'SLA breached',
          value: displayValue('slaBreached'),
          titleStyle: rejectedRequestsStyle,
          icon: <AlertTriangle />,
          iconColor: rejectedRequestsStyle.color,
        }
      );
    }

    if (showDraftKpi) {
      cards.push({
        key: 'drafts',
        title: 'Drafts',
        value: displayValue('drafts'),
        icon: <User2 />,
      });
    }

    return cards;
  }, [showSlaKpis, showDraftKpi, loading, kpis]);

  if (!showReviewKpis) {
    return (
      <div className={styles.dashboard}>
        <Typography variant="subheadline" gutterBottom>
          Welcome
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <DashboardDateRangeFilter value={dateFilter} onChange={setDateFilter} />

      <div className={styles.kpiRow}>
        {kpiCards.map((card) => (
          <KpiCard
            key={card.key}
            title={card.title}
            value={card.value}
            cardVariant={card.cardVariant}
            titleStyle={card.titleStyle}
            icon={card.icon}
            iconColor={card.iconColor}
          />
        ))}
      </div>

      <HomeDashboardChartsSection
        persona={persona}
        dateFilter={dateFilter}
        statusBarData={statusBarData}
        priorityBarData={priorityBarData}
        approvalRatePercent={kpis.approvalRatePercent}
        slaAtRiskRows={slaAtRiskRows}
        showBlueprintCharts={showSlaKpis}
        showPaymentCharts={showPaymentCharts}
        summaryReady={!loading}
      />
    </div>
  );
}

export default HomeDashboard;
